import React from 'react'
import PropTypes from 'prop-types'
import {
  Table, Icon, Tag, Input, Select,
  DatePicker, Button, Card, Row, Col,
  Tooltip,
} from 'antd'
import moment from 'moment'
import history from 'utils/history'

// status terminology + badge color used by list & detail
export const STATUS_META = {
  inquiry: { label: 'Inquiry', color: 'orange', icon: 'clock-circle' },
  pending: { label: 'Inquiry', color: 'orange', icon: 'clock-circle' }, // detail API maps inquiry -> pending
  approved: { label: 'Approved', color: 'green', icon: 'check-circle' },
  rejected: { label: 'Rejected', color: 'red', icon: 'close-circle' },
}

export const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status]
  if (!meta) return <Tag>{status || '-'}</Tag>
  return <Tag color={meta.color}>{meta.label}</Tag>
}

StatusBadge.propTypes = {
  status: PropTypes.string,
}

export const formatDateTime = value => (value ? moment(value).format('DD MMM YYYY HH:mm') : '-')

// UUID is the request id; show the first block, full value on hover
const shortId = id => (id ? id.split('-')[0].toUpperCase() : '-')

const CARD_COLORS = {
  inquiry: '#fa8c16',
  approved: '#52c41a',
  rejected: '#f5222d',
}

const UpdateDataList = ({
  list, summary, stateSearch,
  handlePage, handleChangeSearch, handleChangeStatus,
  handleChangeDate, handleSearch, handleReset, handleFilterStatus,
}) => {
  const columns = [
    {
      title: 'No',
      dataIndex: '',
      key: 'No',
      width: 60,
      render: (text, record, index) => ((list.meta.per_page || 10) * ((list.meta.current_page || 1) - 1)) + index + 1,
    },
    {
      title: 'Request ID',
      dataIndex: 'id',
      key: 'id',
      render: text => (
        <Tooltip title={text}>
          <span style={{ fontFamily: 'monospace' }}>{shortId(text)}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Nama Peserta',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <div>{text || '-'}</div>
          <small className="text-muted">{record.registered_number || '-'}</small>
        </div>
      ),
    },
    {
      title: 'Tanggal Pengajuan',
      dataIndex: 'submitted_at',
      key: 'submitted_at',
      render: text => formatDateTime(text),
    },
    {
      title: 'Tanggal Review',
      dataIndex: 'reviewed_at',
      key: 'reviewed_at',
      render: text => formatDateTime(text),
    },
    {
      title: 'Review Oleh',
      dataIndex: 'reviewed_by',
      key: 'reviewed_by',
      render: reviewer => (reviewer && reviewer.name ? reviewer.name : '-'),
    },
    {
      title: 'Catatan Reviewer',
      dataIndex: 'notes',
      key: 'notes',
      width: 240,
      render: text => (text ? (
        <Tooltip title={text}>
          <div style={{
            maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
          >
            {text}
          </div>
        </Tooltip>
      ) : '-'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: text => <StatusBadge status={text} />,
    },
    {
      title: 'Aksi',
      dataIndex: '',
      key: 'action',
      width: 80,
      render: (text, record) => (
        <div className="d-flex justify-content-around">
          <Icon type="eye" className="cursor-pointer" onClick={() => history.push(`/update-data/${record.id}/detail`)} />
        </div>
      ),
    },
  ]

  const statusList = ['inquiry', 'approved', 'rejected']

  const dateRange = stateSearch.date_from && stateSearch.date_to
    ? [moment(stateSearch.date_from, 'YYYY-MM-DD'), moment(stateSearch.date_to, 'YYYY-MM-DD')]
    : []

  return (
    <div>
      <p className="text-muted mb-3">Kelola dan pantau pengajuan pengkinian data peserta</p>

      <Row gutter={16} className="mb-3">
        {statusList.map(status => (
          <Col xs={24} md={8} key={status} className="mb-2">
            <Card
              hoverable
              onClick={() => handleFilterStatus(status)}
              bodyStyle={{ padding: '16px 20px' }}
              style={{
                borderRadius: 10,
                borderLeft: `4px solid ${CARD_COLORS[status]}`,
                borderColor: stateSearch.status === status ? CARD_COLORS[status] : undefined,
              }}
            >
              <div className="d-flex align-items-center">
                <Icon
                  type={STATUS_META[status].icon}
                  theme="twoTone"
                  twoToneColor={CARD_COLORS[status]}
                  style={{ fontSize: 32 }}
                  className="mr-3"
                />
                <div>
                  <h3 className="mb-0 font-weight-bold">
                    {summary.isFetching ? '-' : summary.data[status]}
                  </h3>
                  <span className="text-muted">{STATUS_META[status].label}</span>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <div className="pb-3">
        <div className="d-flex flex-wrap align-items-center btn-primary-wrapper">
          <Select
            placeholder="Status"
            style={{ width: 150 }}
            className="mr-2 mb-2"
            onChange={handleChangeStatus}
            value={stateSearch.status || undefined}
            allowClear
          >
            {statusList.map(item => (
              <Select.Option key={item} value={item}>{STATUS_META[item].label}</Select.Option>
            ))}
          </Select>
          <Input
            placeholder="Cari Request ID, Nama Peserta..."
            style={{ width: 260 }}
            className="mr-2 mb-2"
            allowClear
            value={stateSearch.search || ''}
            onChange={handleChangeSearch}
            onPressEnter={handleSearch}
          />
          <DatePicker.RangePicker
            format="DD/MM/YYYY"
            placeholder={['Dari', 'Sampai']}
            className="mr-2 mb-2"
            value={dateRange}
            onChange={handleChangeDate}
          />
          <Button type="primary" icon="search" className="mr-2 mb-2" onClick={handleSearch}>Cari</Button>
          <Button className="mb-2" onClick={handleReset}>Reset</Button>
        </div>
      </div>

      <Table
        rowKey="id"
        className="table-striped-rows"
        columns={columns}
        dataSource={list.data}
        loading={list.isFetching}
        scroll={{ x: 'max-content' }}
        pagination={{
          showTotal: (total, range) => `Showing ${range[0]} to ${range[1]} of ${total} entries`,
          total: list.meta ? list.meta.total_count : list.data.length,
          current: list.meta ? list.meta.current_page : 1,
          defaultPageSize: 10,
          onChange: handlePage,
        }}
        rowClassName={record => (record.is_error ? 'text-danger' : '')}
      />
    </div>
  )
}

UpdateDataList.propTypes = {
  list: PropTypes.object,
  summary: PropTypes.object,
  stateSearch: PropTypes.object,
  handlePage: PropTypes.func,
  handleChangeSearch: PropTypes.func,
  handleChangeStatus: PropTypes.func,
  handleChangeDate: PropTypes.func,
  handleSearch: PropTypes.func,
  handleReset: PropTypes.func,
  handleFilterStatus: PropTypes.func,
}

export default UpdateDataList
